import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Stack
} from '@mui/material';
import {
  Add,
  Remove,
  CheckCircle,
  Warning,
  Error,
  Info,
  CalendarToday,
  People,
  CreditCard,
  Download,
  Receipt
} from '@mui/icons-material';
import { useParams, useSearchParams } from 'react-router-dom';
import apiService from '../services/api';
import SeatManagementDialog from '../components/SeatManagementDialog';
import TransactionList from '../components/TransactionList';
import { useDateFormat } from '../hooks/useDateFormat';

interface Subscription {
  id: string;
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete';
  seatsPurchased: number;
  seatsUsed: number;
  trialEnd?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  paymentProviderId?: string;
}

interface SeatUsage {
  purchased: number;
  used: number;
  available: number;
}

interface TrialStatus {
  isTrial: boolean;
  trialEnd?: string;
  daysLeft: number;
}

interface SubscriptionsProps {
  selectedOrganizationId?: string;
}

const Subscriptions: React.FC<SubscriptionsProps> = ({ selectedOrganizationId }) => {
  const { organizationId: urlOrganizationId } = useParams<{ organizationId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // For success URLs, prioritize URL-based organization to prevent organization selector override
  const hasSuccessParams = searchParams.get('success') === 'true' || searchParams.get('canceled') === 'true';
  const organizationId = hasSuccessParams ? urlOrganizationId : (selectedOrganizationId || urlOrganizationId);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [seatUsage, setSeatUsage] = useState<SeatUsage | null>(null);
  const [trialStatus, setTrialStatus] = useState<TrialStatus | null>(null);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const [loading, setLoading] = useState(true);
  const { formatDateOnly } = useDateFormat();
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Seat management dialog
  const [seatManagementDialog, setSeatManagementDialog] = useState(false);
  const [seatManagementMode, setSeatManagementMode] = useState<'add' | 'remove'>('add');
  const [managingSeats, setManagingSeats] = useState(false);
  
  // Start trial dialog
  const [startTrialDialog, setStartTrialDialog] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  
  // Cancel trial dialog
  const [cancelTrialDialog, setCancelTrialDialog] = useState(false);
  const [cancelingTrial, setCancelingTrial] = useState(false);

  // Start paid subscription dialog
  const [startPaidSubscriptionDialog, setStartPaidSubscriptionDialog] = useState(false);
  const [paidSeats, setPaidSeats] = useState(1);
  const [startingPaidSubscription, setStartingPaidSubscription] = useState(false);

  useEffect(() => {
    if (organizationId) {
      fetchSubscriptionData();
    }
  }, [organizationId]);

  // Refetch data when selectedOrganizationId changes (from org selector)
  useEffect(() => {
    if (selectedOrganizationId && selectedOrganizationId !== urlOrganizationId) {
      fetchSubscriptionData();
    }
  }, [selectedOrganizationId]);

  // Handle URL parameters for success/cancel states - run immediately on mount
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    
    if (success === 'true') {
      setSuccessMessage('Payment successful! Your subscription has been activated.');
      // Clear the URL parameters after showing the message
      setSearchParams({});
      // Don't try to fetch data yet - wait for organizationId to be available
    } else if (canceled === 'true') {
      setError('Payment was canceled. You can try again anytime.');
      // Clear the URL parameters
      setSearchParams({});
    }
  }, []); // Empty dependency array - run only once on mount

  // Refresh subscription data when organizationId becomes available and we have a success message
  useEffect(() => {
    if (organizationId && successMessage) {
      fetchSubscriptionData();
    }
  }, [organizationId, successMessage]);

  const fetchSubscriptionData = async () => {
    if (!organizationId) return;
    
    try {
      setLoading(true);
      const response = await apiService.getSubscription(organizationId);
      setSubscription(response.subscription);
      setSeatUsage(response.seatUsage);
      setTrialStatus(response.trialStatus);
      setHasUsedTrial(response.hasUsedTrial || false);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleSeatManagement = async (newSeatCount: number) => {
    try {
      setManagingSeats(true);
      
      if (!subscription?.id) {
        throw 'No active subscription found';
      }

      // Update seats via Stripe API
      await apiService.updateSubscriptionSeats(subscription.id, newSeatCount);
      
      setSeatManagementDialog(false);
      setSuccessMessage(`Successfully updated to ${newSeatCount} seats`);
      await fetchSubscriptionData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update seats');
    } finally {
      setManagingSeats(false);
    }
  };

  const openSeatManagement = () => {
    setSeatManagementMode('add'); // Always use 'add' mode for unified interface
    setSeatManagementDialog(true);
  };

  const handleStartTrial = async () => {
    try {
      setStartingTrial(true);
      await apiService.createTrialSubscription(organizationId!);
      setStartTrialDialog(false);
      await fetchSubscriptionData();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to start trial';
      setError(errorMessage);
      // If trial was already used, refresh data to update hasUsedTrial flag
      if (errorMessage.includes('Trial has already been used')) {
        await fetchSubscriptionData();
      }
    } finally {
      setStartingTrial(false);
    }
  };

  const handleCancelTrial = async () => {
    try {
      setCancelingTrial(true);
      if (subscription?.id) {
        await apiService.cancelSubscription(subscription.id);
        setCancelTrialDialog(false);
        await fetchSubscriptionData();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to cancel trial');
    } finally {
      setCancelingTrial(false);
    }
  };

  const handleStartPaidSubscription = async () => {
    try {
      setStartingPaidSubscription(true);
      const response = await apiService.createPaidSubscription(organizationId!, paidSeats);
      
      if (response.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = response.checkoutUrl;
      } else {
        setError('No checkout URL received');
        setStartingPaidSubscription(false);
        return;
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start paid subscription');
      setStartingPaidSubscription(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
        return 'warning';
      case 'canceled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle color="success" />;
      case 'trialing':
        return <CheckCircle color="info" />;
      case 'past_due':
        return <Warning color="warning" />;
      case 'canceled':
        return <Error color="error" />;
      default:
        return <Info />;
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const canManageSeats = subscription && (subscription.status === 'active' || subscription.status === 'trialing');
  const isTrial = trialStatus?.isTrial;
  const nextBillingDate = subscription?.currentPeriodEnd;

  return (
    <Box p={3}>
      <Typography variant="h4" component="h1" gutterBottom>
        Seats & Billing
      </Typography>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Current Status Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                {subscription && getStatusIcon(subscription.status)}
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {subscription ? 'Active Subscription' : 'No Subscription'}
                  </Typography>
                  {subscription && (
                    <Chip
                      label={subscription.status.toUpperCase()}
                      color={getStatusColor(subscription.status) as any}
                      size="small"
                    />
                  )}
                </Box>
              </Box>
              
              {isTrial && trialStatus && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Trial Period:</strong> {trialStatus.daysLeft} days remaining
                  </Typography>
                  {trialStatus.trialEnd && (
                    <Typography variant="body2">
                      Trial ends: {formatDateOnly(trialStatus.trialEnd)}
                    </Typography>
                  )}
                </Alert>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Box textAlign="center">
                <Typography variant="h2" color="primary" gutterBottom>
                  {isTrial ? 'Unlimited' : (seatUsage?.purchased || 0)}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Seats Purchased
                </Typography>
                {seatUsage && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {isTrial ? (
                      `${seatUsage.used} active • Unlimited available`
                    ) : (
                      `${seatUsage.used} active • ${seatUsage.available} available`
                    )}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Billing Information */}
      {nextBillingDate && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <CalendarToday color="primary" />
              <Box>
                <Typography variant="h6" gutterBottom>
                  Next Billing Cycle
                </Typography>
                <Typography variant="body1">
                  {formatDateOnly(nextBillingDate)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your next invoice will be generated on this date
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Seat Management Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Manage Seats
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Each seat costs $15/month. Add or remove seats as needed.
          </Typography>
          
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {!subscription ? (
              <>
                {!hasUsedTrial && (
                  <Button
                    variant="contained"
                    startIcon={<CheckCircle />}
                    onClick={() => setStartTrialDialog(true)}
                    size="large"
                    color="primary"
                  >
                    Start 30-Day Free Trial
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<CreditCard />}
                  onClick={() => setStartPaidSubscriptionDialog(true)}
                  size="large"
                  color="success"
                >
                  Start Paid Subscription
                </Button>
              </>
            ) : subscription.status === 'canceled' ? (
              <>
                {!hasUsedTrial && (
                  <Button
                    variant="contained"
                    startIcon={<CheckCircle />}
                    onClick={() => setStartTrialDialog(true)}
                    size="large"
                    color="primary"
                  >
                    Start 30-Day Free Trial
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<CreditCard />}
                  onClick={() => setStartPaidSubscriptionDialog(true)}
                  size="large"
                  color="success"
                >
                  Start Paid Subscription
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={<People />}
                  onClick={openSeatManagement}
                  disabled={!canManageSeats}
                  size="large"
                >
                  Manage Seats
                </Button>
                {subscription.status === 'trialing' && (
                  <Button
                    variant="outlined"
                    startIcon={<Warning />}
                    onClick={() => setCancelTrialDialog(true)}
                    size="large"
                    color="error"
                  >
                    Cancel Trial
                  </Button>
                )}
              </>
            )}
          </Stack>

          {hasUsedTrial && !subscription && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Trial Already Used:</strong> This organization has already used its 30-day free trial. Please start a paid subscription to continue using TimeSentinel.
              </Typography>
            </Alert>
          )}
          {!canManageSeats && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                {!subscription 
                  ? 'No subscription found. Please contact support to set up your subscription.'
                  : subscription.status === 'canceled' 
                    ? 'Subscription is canceled. Reactivate to manage seats.'
                    : subscription.status === 'past_due'
                      ? 'Subscription is past due. Please update your payment method.'
                      : 'Subscription status does not allow seat management.'
                }
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Seat Management Dialog */}
      <SeatManagementDialog
        open={seatManagementDialog}
        onClose={() => setSeatManagementDialog(false)}
        onConfirm={handleSeatManagement}
        currentSeats={seatUsage?.purchased || 0}
        usedSeats={seatUsage?.used || 0}
        mode={seatManagementMode}
        loading={managingSeats}
        pricePerSeat={15}
      />

      {/* Transaction History */}
      {subscription && organizationId && (
        <Box mt={4}>
          <TransactionList organizationId={organizationId} />
        </Box>
      )}

      {/* Start Trial Dialog */}
      <Dialog open={startTrialDialog} onClose={() => setStartTrialDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Start Free Trial</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Start your 30-day free trial to get full access to TimeSentinel features.
          </Typography>
          <Box mt={3}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>What's included in your trial:</strong>
              </Typography>
              <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                • Unlimited users during trial period<br/>
                • Full access to all features<br/>
                • No credit card required<br/>
                • 30 days to explore and test
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartTrialDialog(false)}>Cancel</Button>
          <Button
            onClick={handleStartTrial}
            variant="contained"
            color="primary"
            disabled={startingTrial}
          >
            {startingTrial ? 'Starting Trial...' : 'Start Free Trial'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Trial Dialog */}
      <Dialog open={cancelTrialDialog} onClose={() => setCancelTrialDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Trial</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> This action cannot be undone.
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Are you sure you want to cancel your trial? This will immediately end your trial period and you will lose access to all premium features.
          </Typography>
          <Box mt={3}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>What happens when you cancel:</strong>
              </Typography>
              <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                • Trial ends immediately<br/>
                • All users lose access to premium features<br/>
                • You cannot restart the trial<br/>
                • You'll need to contact support to reactivate
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTrialDialog(false)}>Keep Trial</Button>
          <Button
            onClick={handleCancelTrial}
            variant="contained"
            color="error"
            disabled={cancelingTrial}
          >
            {cancelingTrial ? 'Canceling...' : 'Yes, Cancel Trial'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Start Paid Subscription Dialog */}
      <Dialog open={startPaidSubscriptionDialog} onClose={() => setStartPaidSubscriptionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Start Paid Subscription</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Start a paid subscription to get full access to TimeSentinel features.
          </Typography>
          <Box mt={3}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Pricing:</strong> $15 per seat per month
              </Typography>
            </Alert>
            <FormControl fullWidth>
              <InputLabel>Number of Seats</InputLabel>
              <Select
                value={paidSeats}
                onChange={(e) => setPaidSeats(Number(e.target.value))}
                label="Number of Seats"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 25, 30, 40, 50].map((num) => (
                  <MenuItem key={num} value={num}>
                    {num} seat{num !== 1 ? 's' : ''} - ${(num * 15).toLocaleString()}/month
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box mt={2}>
              <Alert severity="success">
                <Typography variant="body2">
                  <strong>Total:</strong> ${(paidSeats * 15).toLocaleString()} per month
                </Typography>
              </Alert>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartPaidSubscriptionDialog(false)}>Cancel</Button>
          <Button
            onClick={handleStartPaidSubscription}
            variant="contained"
            disabled={startingPaidSubscription}
          >
            {startingPaidSubscription ? 'Starting...' : 'Start Paid Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Subscriptions;
